import { createRef, useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { Config, ICenter } from '../interfaces';
import '../styles/App.css';

declare const config: Config;

interface Props {
  selectedCenter: ICenter | undefined;
  selectedLanguage: string;
}

interface LocationState {
  voucherId?: string;
  performance?: {
    id: string;
  };
  productId?: string;
}

const Webshop = ({ selectedLanguage }: Props) => {
  const iframeRef = createRef<HTMLIFrameElement>();
  const location = useLocation<LocationState>();

  const pathParts = location.pathname.split('/');
  const centerId = pathParts[2];
  let page = pathParts[3] || '';
  if (page === 'withdialog' && pathParts[4]) {
    page = `withdialog/${pathParts[4]}`;
  }

  useEffect(() => {
    const compesoApi = window.Compeso;

    if (
      compesoApi?.updateOrInitializeIFrameResizer &&
      typeof compesoApi.updateOrInitializeIFrameResizer === 'function'
    ) {
      compesoApi.updateOrInitializeIFrameResizer();
    }
    if (compesoApi?.loadContent && typeof compesoApi.loadContent === 'function') {
      compesoApi.loadContent(config.apiService.webshop.endpoint);
    }
  }, []);
  const getIFrameData = (page: string) => {
    let url = `${config.apiService.webshop.endpoint}/landingpage?center=${centerId}&language=${selectedLanguage}`;

    switch (page) {
      case 'performance-list':
        url = `${url}&page=home`;
        break;
      case 'product-list':
        url = `${url}&page=productlist`;
        break;
      case 'withdialog/product-list': {
        const productListParams = new URLSearchParams({
          page: 'productlist',
          displayoption: 'dialog',
          closeurl: `${window.location.origin}/withdialog/product-list`,
        });
        url = `${url}&${productListParams.toString()}`;
        break;
      }
      case 'retail-list':
        url = `${url}&page=retaillist`;
        break;
      case 'withdialog/retail-list': {
        const retailListParams = new URLSearchParams({
          page: 'retaillist',
          displayoption: 'dialog',
          closeurl: `${window.location.origin}/withdialog/retail-list`,
        });
        url = `${url}&${retailListParams.toString()}`;
        break;
      }
      case 'retail-product': {
        if (!location.state.productId) {
          return <Redirect to='/retail-details' />;
        }
        const retailProductParams = new URLSearchParams({
          page: 'retailproduct',
          productId: location.state.productId,
        });
        url = `${url}&${retailProductParams.toString()}`;
        break;
      }
      case 'registration':
      case 'withdialog/registration':
        url = `${url}&page=registration`;
        break;
      case 'customerverify':
        url = `${url}&page=customerverify`;
        break;
      case 'login':
      case 'withdialog/login':
        url = `${url}&page=login`;
        break;
      case 'logout':
      case 'withdialog/logout':
        url = `${url}&page=logout`;
        break;
      case 'my-account':
      case 'withdialog/my-account':
        url = `${url}&page=myaccount`;
        break;
      case 'loyalty-rewards':
      case 'withdialog/loyalty-rewards':
        url = `${url}&page=loyaltyrewards`;
        break;
      case 'transaction-history':
      case 'withdialog/transaction-history':
        url = `${url}&page=transactionhistory`;
        break;
      case 'purchase-voucher': {
        if (!location.state.productId) {
          return <Redirect to='/voucher-list' />;
        }
        const productDetailsParams = new URLSearchParams({
          page: 'productdetails',
          productId: location.state.productId,
        });
        url = `${url}&${productDetailsParams.toString()}`;
        break;
      }
      case 'seatingplan': {
        if (!location.state.performance?.id) {
          return <Redirect to='/' />;
        }
        const seatingPlanParams = new URLSearchParams({
          page: 'seatingplan',
          performance: location.state.performance.id,
        });
        url = `${url}&${seatingPlanParams.toString()}`;
        break;
      }
      case 'checkout':
      case 'withdialog/checkout':
        url = `${url}&page=checkout`;
        break;
      case 'privacy':
      case 'withdialog/privacy':
        url = `${url}&page=privacy`;
        break;
      case 'imprint':
      case 'withdialog/imprint':
        url = `${url}&page=imprint`;
        break;
      case 'faq':
        url = `${url}&page=faq`;
        break;
      case 'contact':
      case 'withdialog/contact':
        url = `${url}&page=contact`;
        break;
      case 'terms':
      case 'withdialog/terms':
        url = `${url}&page=terms`;
        break;
      case 'loyalty':
      case 'withdialog/loyalty':
        url = `${url}&page=loyalty`;
        break;
      case 'loyaltydetails':
        url = `${url}&page=loyaltydetails`;
        break;
      case 'voucher-verification':
        url = `${url}&page=voucherverification`;
        break;
      case 'consents':
      case 'withdialog/consents':
        url = `${url}&page=consents`;
        break;
      case 'newsletter':
      case 'withdialog/newsletter':
        url = `${url}&page=newsletter`;
        break;
      case 'withdialog/film-list': {
        const filmListParams = new URLSearchParams({
          page: 'home',
          displayoption: 'dialog',
          closeurl: `${window.location.origin}/center/${centerId}/withdialog/film-list`,
        });
        url = `${url}&${filmListParams.toString()}`;
        break;
      }
      case 'withdialog': {
        const withdialogParams = new URLSearchParams({
          page: 'home',
          displayoption: 'dialog',
          closeurl: `${window.location.origin}/center/${centerId}/withdialog/film-list`,
        });
        url = `${url}&${withdialogParams.toString()}`;
        break;
      }
      case '':
        url = `${url}&page=home`;
        break;
      default:
        url = '';
        break;
    }
    return (
      <div className='container'>
        <div className='frame'>
          <iframe
            id='compeso-performance'
            className='iframe-webshop'
            title='iframe-webshop'
            src={url}
            ref={iframeRef}
            allow='payment; camera; clipboard-read; clipboard-write'
            loading='lazy'
          >
            iframe content
          </iframe>
        </div>
      </div>
    );
  };

  if (!centerId || !page) {
    return <Redirect to='/center-selection' />;
  }

  return getIFrameData(page);
};

export default Webshop;
